import { ActionTree } from 'vuex';
import {
  ManifestService,
  storageService,
  localeService,
  getLinkInfo,
  getOrganization,
  EDirection,
  authService,
} from '@/shared/services';
import { configs } from '@/shared/configs';
import { init } from '@/shared/providers';
import extractUrlInfo, { IUrlInfo } from '../utils/url-parser';

import SykFieldInfo from '../classes/SykFieldInfo';
import { ILoginStore, IMetaInfo } from './shared.types';
import { RRP_SYK_PREFIX } from './constants';
import { getAccessSessionConfiguration } from '@/modules/login/services/access-service';
import { AllowedLocales } from '@/shared/services/locale/localeService';
import { AccessStatusOriginal } from '@/app.manager/types/AccessStatus';
import { useNavigationProvider } from '@/shared/helpers/helpers';

const sharedActions: ActionTree<ILoginStore, any> = {
  async getInitialData({ commit, dispatch }, params: URLSearchParams): Promise<any> {
    const metaInfo = await dispatch('initMetaInfo', params);

    await dispatch('initLocale', metaInfo.locale);

    const organization = await dispatch('getOrganization');

    storageService.session.set(configs.COLOR_SCHEMA_KEY, organization.colorSchema);
    storageService.session.set(configs.ORGANIZATION_LOGO_KEY, organization?.logo?.url);
    await dispatch('getAccessSessionConfiguration', metaInfo);
  },

  initLocale({ commit, dispatch }, locale: string) {
    const direction = locale === 'he' ? EDirection.RTL : EDirection.LTR;

    localeService.setLang(locale as AllowedLocales);

    dispatch('setDirection', direction, { root: true });
  },

  async initMetaInfo({ commit, dispatch }, params): Promise<IMetaInfo> {
    let urlInfo: IUrlInfo = {};

    // Parse URL parameters
    try {
      urlInfo = extractUrlInfo(params);
    } catch (error) {
      throw Error('Invalid URL parameters');
    }

    // Check if we have linkID
    if (!urlInfo.linkId) {
      throw Error('Missing URL parameters');
    }

    // Fetch Meta Info of the user using linkID
    const metaInfo = await dispatch('loadLinkInfo', urlInfo.linkId);

    return {
      ...metaInfo,
    };
  },

  async loadLinkInfo({ commit }, linkId: string) {
    const { payload, type } = await getLinkInfo(linkId);

    payload.appType = ['RESULTS', 'PRETEST'].includes(type) ? 'RESULTS' : 'ACCESS';
    commit('setMetaInfo', payload);
    // links-manager api sends 'lang' for /register links, and 'locale' for /login links (not cool)
    // and both modules use this method.
    commit('setLocale', payload.lang || payload.locale);

    const isSessionAborted = payload?.status?.toString() === AccessStatusOriginal.ABORTED.toString();

    if (isSessionAborted) {
      useNavigationProvider().isNavigationHidden = true;
    }

    return payload;
  },

  async getOrganization({ commit }) {
    const organization = await getOrganization();

    commit('setOrganization', organization);

    return organization;
  },

  async getAccessSessionConfiguration({ commit, dispatch }, metaInfo: IMetaInfo) {
    const { config, baseUrl } = await getAccessSessionConfiguration(metaInfo.accessSessionId);

    // update signature, use new baseUrl
    const manifestInstance = new ManifestService(config, metaInfo.locale, baseUrl);

    authService.setSessionId(metaInfo.accessSessionId);
    const { locale, rrp } = await manifestInstance.rrpConfigs;
    const { sykLoginConfig, hideIgentifyLogo, documents } = rrp;

    dispatch('setHideIgentifyLogo', !!hideIgentifyLogo, { root: true });
    dispatch('setSupportsLandscapeWarning', !!config?.supportedFeatures?.landscapeWarning, { root: true });

    if (documents) {
      commit('setDocuments', documents);
    }

    if (sykLoginConfig) {
      SykFieldInfo.mapTranslationKeys(sykLoginConfig, RRP_SYK_PREFIX);
      commit('loginModule/verificationModule/setSykInfo', sykLoginConfig, { root: true });
    }

    if (manifestInstance.globalStoredInUIAssets) {
      await localeService.getGlobalTranslations(metaInfo.locale);
    }

    if (locale) {
      localeService.addTranslation(metaInfo.locale, {
        [RRP_SYK_PREFIX]: locale.login.syk,
        ...locale,
        ...locale.login,
      });
    }

    // initialize validation translations
    await init(metaInfo.locale, localeService.getValidationTranslations(metaInfo.locale));

    return manifestInstance;
  },
};

export default sharedActions;
